/* ── Dark Mode CSS — Deep Space 2026 ── */
export const darkModeCSS = `
  /* ══════════════════════════════════════════════════════
     BLITZ CRM v30.06 — "Aurora" Design System
     Deep dark base · Vivid accent palette · Glass surfaces
  ══════════════════════════════════════════════════════ */

  :root {
    --void:          #05070F;
    --deep:          #090D1A;
    --base:          #0D1220;
    --surface:       #121829;
    --elevated:      #182035;
    --overlay:       #1E2840;
    --rim:           rgba(99,179,237,0.10);
    --rim-bright:    rgba(99,179,237,0.22);
    --rim-glow:      rgba(99,179,237,0.35);
    --cyan:   #38BDF8;
    --blue:   #60A5FA;
    --indigo: #818CF8;
    --violet: #A78BFA;
    --pink:   #F472B6;
    --rose:   #FB7185;
    --amber:  #FBBF24;
    --lime:   #A3E635;
    --teal:   #2DD4BF;
    --green:  #34D399;
    --orange: #FB923C;
    --t1: #F0F6FF;
    --t2: #9BB3CC;
    --t3: #5C7A99;
    --t4: #3A5068;
    --grad-header: linear-gradient(180deg, rgba(9,13,26,0.97) 0%, rgba(9,13,26,0.93) 100%);
    --grad-card:   linear-gradient(135deg, rgba(18,24,41,0.9) 0%, rgba(12,17,32,0.95) 100%);
    --shadow-sm:  0 2px 8px rgba(0,0,0,0.35);
    --shadow-md:  0 4px 24px rgba(0,0,0,0.45), 0 1px 4px rgba(0,0,0,0.3);
    --shadow-lg:  0 8px 48px rgba(0,0,0,0.55), 0 2px 12px rgba(0,0,0,0.4);
    --shadow-xl:  0 20px 80px rgba(0,0,0,0.65), 0 4px 20px rgba(0,0,0,0.5);
    --glow-cyan:  0 0 24px rgba(56,189,248,0.18), 0 0 60px rgba(56,189,248,0.06);
    --r-xs: 6px; --r-sm: 8px; --r-md: 12px; --r-lg: 16px; --r-xl: 20px; --r-2xl: 28px;
    --ease: cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes slideIn    { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes fadeUp     { from { opacity: 0; transform: translateY(14px) scale(0.99); } to { opacity: 1; transform: translateY(0) scale(1); } }
  @keyframes fadeIn     { from { opacity: 0; } to { opacity: 1; } }
  @keyframes pulse      { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
  @keyframes shimmer    { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes rowIn      { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes glowPulse  { 0%,100% { box-shadow: var(--glow-cyan); } 50% { box-shadow: 0 0 40px rgba(56,189,248,0.28), 0 0 80px rgba(56,189,248,0.1); } }
  @keyframes float      { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
  @keyframes stagger-in { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
  @keyframes border-flow { 0%,100% { border-color: var(--rim); } 50% { border-color: var(--rim-bright); } }
  @keyframes spin        { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes pulse-glow  { 0%,100% { box-shadow: var(--glow-cyan); } 50% { box-shadow: 0 0 32px rgba(56,189,248,0.3); } }

  .dark-mode {
    background: var(--void) !important;
    color: var(--t1) !important;
    font-family: 'Inter', 'Plus Jakarta Sans', 'Segoe UI', system-ui, sans-serif !important;
    font-feature-settings: 'cv02','cv03','cv04','cv11','ss01';
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  .dark-mode * {
    transition: background 0.2s var(--ease), border-color 0.2s var(--ease), color 0.15s var(--ease), box-shadow 0.2s var(--ease);
    box-sizing: border-box;
  }

  .dark-mode ::-webkit-scrollbar { width: 5px; height: 5px; }
  .dark-mode ::-webkit-scrollbar-track { background: var(--void); }
  .dark-mode ::-webkit-scrollbar-thumb { background: rgba(99,179,237,0.18); border-radius: 3px; }
  .dark-mode ::-webkit-scrollbar-thumb:hover { background: rgba(99,179,237,0.35); }
  .dark-mode ::selection { background: rgba(56,189,248,0.28); color: #FFF; }

  /* ══ AUTO-B2026 — Always white, never dark ══ */
  .dark-mode .autob2026-light,
  .dark-mode .autob2026-light * {
    background-color: unset !important;
    color: unset !important;
    border-color: unset !important;
    box-shadow: unset !important;
  }
  .dark-mode .autob2026-light { background: #FFFFFF !important; color: #0F172A !important; }
  .dark-mode .autob2026-light input,
  .dark-mode .autob2026-light select,
  .dark-mode .autob2026-light textarea {
    background: #FFFFFF !important;
    color: #0F172A !important;
    border: 1px solid #CBD5E1 !important;
  }
  .dark-mode .autob2026-light table { background: #FFFFFF !important; color: #0F172A !important; border-color: #E2E8F0 !important; }
  .dark-mode .autob2026-light table thead tr { background: #F8FAFC !important; }
  .dark-mode .autob2026-light table thead th { color: #475569 !important; border-color: #CBD5E1 !important; background: #F1F5F9 !important; }
  .dark-mode .autob2026-light table tbody tr { background: #FFFFFF !important; border-color: #E2E8F0 !important; }
  .dark-mode .autob2026-light table tbody tr:hover { background: #F8FAFF !important; box-shadow: none !important; }
  .dark-mode .autob2026-light table td { color: #334155 !important; border-color: #E2E8F0 !important; background: transparent !important; }
  .dark-mode .autob2026-light table td:first-child { color: #0F172A !important; }
  .dark-mode .autob2026-light button { box-shadow: none !important; }
  .dark-mode .autob2026-light [style*="background: #0F172A"],
  .dark-mode .autob2026-light [style*="background:#0F172A"] { background: #0F172A !important; color: #A3E635 !important; }

  .dark-mode .blitz-header {
    background: var(--grad-header) !important;
    backdrop-filter: blur(24px) saturate(200%) !important;
    -webkit-backdrop-filter: blur(24px) saturate(200%) !important;
    border-bottom: 1px solid var(--rim) !important;
    box-shadow: 0 1px 0 rgba(99,179,237,0.06), 0 4px 40px rgba(0,0,0,0.5) !important;
  }

  .dark-mode .blitz-main { animation: fadeUp 0.35s var(--ease); background: var(--void) !important; }

  .dark-mode input, .dark-mode select, .dark-mode textarea {
    background: var(--base) !important;
    color: var(--t1) !important;
    border: 1.5px solid var(--rim) !important;
    border-radius: var(--r-sm) !important;
    caret-color: var(--cyan);
    font-family: 'Inter', sans-serif !important;
    font-size: 13px !important;
  }
  .dark-mode input:focus, .dark-mode select:focus, .dark-mode textarea:focus {
    border-color: var(--cyan) !important;
    box-shadow: 0 0 0 3px rgba(56,189,248,0.12), 0 0 20px rgba(56,189,248,0.06) !important;
    outline: none !important;
    background: var(--deep) !important;
  }
  .dark-mode input::placeholder { color: var(--t4) !important; }
  .dark-mode select option { background: var(--surface) !important; color: var(--t1) !important; }

  /* ══ TABLES ══ */
  .dark-mode table {
    color: var(--t1) !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    width: 100% !important;
    border: 1px solid var(--rim) !important;
  }
  .dark-mode table thead tr {
    background: linear-gradient(90deg, rgba(14,20,40,0.98) 0%, rgba(18,24,41,0.95) 100%) !important;
  }
  .dark-mode table thead th {
    color: var(--t3) !important;
    text-transform: uppercase !important;
    font-size: 10.5px !important;
    font-weight: 700 !important;
    letter-spacing: 1.2px !important;
    border-bottom: 1px solid var(--rim-bright) !important;
    border-right: 1px solid var(--rim) !important;
    border-top: none !important;
    padding: 11px 14px !important;
    white-space: nowrap;
    background: transparent !important;
  }
  .dark-mode table thead th:last-child { border-right: none !important; }
  .dark-mode table tbody tr {
    border-bottom: 1px solid rgba(99,179,237,0.07) !important;
    animation: rowIn 0.25s var(--ease) both;
  }
  .dark-mode table tbody tr:nth-child(even) { background: rgba(255,255,255,0.013) !important; }
  .dark-mode table tbody tr:hover {
    background: rgba(56,189,248,0.055) !important;
    box-shadow: inset 3px 0 0 0 var(--cyan) !important;
  }
  .dark-mode table td {
    color: var(--t2) !important;
    border-bottom: 1px solid rgba(99,179,237,0.07) !important;
    border-right: 1px solid var(--rim) !important;
    padding: 10px 14px !important;
    font-size: 13px !important;
    vertical-align: middle !important;
  }
  .dark-mode table td:last-child { border-right: none !important; }
  .dark-mode table td:first-child { color: var(--t1) !important; font-weight: 500 !important; }
  .dark-mode table tfoot tr {
    background: rgba(56,189,248,0.04) !important;
    border-top: 1px solid var(--rim-bright) !important;
  }
  .dark-mode table tfoot td {
    border-right: 1px solid var(--rim) !important;
  }
  .dark-mode table tfoot td:last-child { border-right: none !important; }

  /* ══ SURFACES ══ */
  .dark-mode [style*="background: #FFFFFF"], .dark-mode [style*="background:#FFFFFF"],
  .dark-mode [style*="background: white"], .dark-mode [style*="background:white"] {
    background: var(--surface) !important;
    border: 1px solid var(--rim) !important;
    box-shadow: var(--shadow-md) !important;
  }
  .dark-mode [style*="background: #F8FAFC"], .dark-mode [style*="background:#F8FAFC"],
  .dark-mode [style*="background: #F9FAFB"], .dark-mode [style*="background:#F9FAFB"] { background: var(--base) !important; }
  .dark-mode [style*="background: #F1F5F9"], .dark-mode [style*="background:#F1F5F9"] { background: var(--void) !important; }
  .dark-mode [style*="background: #E2E8F0"], .dark-mode [style*="background:#E2E8F0"] { background: var(--elevated) !important; color: var(--t2) !important; }

  /* ══ CARDS ══ */
  .dark-mode [style*="borderRadius: 14"], .dark-mode [style*="borderRadius: 16"], .dark-mode [style*="borderRadius: 12"] {
    backdrop-filter: blur(16px) saturate(160%) !important;
    -webkit-backdrop-filter: blur(16px) saturate(160%) !important;
  }
  .dark-mode [style*="borderRadius: 14"]:hover, .dark-mode [style*="borderRadius: 16"]:hover {
    border-color: var(--rim-bright) !important;
    box-shadow: var(--glow-cyan), var(--shadow-md) !important;
    transform: translateY(-1px);
  }

  /* ══ BORDERS ══ */
  .dark-mode [style*="border: 1px solid #E2E8F0"], .dark-mode [style*="border:1px solid #E2E8F0"],
  .dark-mode [style*="border: 1px solid #CBD5E1"], .dark-mode [style*="borderColor: #E2E8F0"],
  .dark-mode [style*="border-color: #E2E8F0"] { border-color: var(--rim) !important; }
  .dark-mode [style*="border-bottom: 2px solid #E2E8F0"], .dark-mode [style*="borderBottom: 2px solid #E2E8F0"] { border-color: var(--rim-bright) !important; }
  .dark-mode [style*="border-bottom: 1px solid #E2E8F0"], .dark-mode [style*="borderBottom: 1px solid #E2E8F0"] { border-color: var(--rim) !important; }

  /* ══ TEXT ══ */
  .dark-mode [style*="color: #0F172A"], .dark-mode [style*="color:#0F172A"],
  .dark-mode [style*="color: #1E293B"], .dark-mode [style*="color:#1E293B"] { color: var(--t1) !important; }
  .dark-mode [style*="color: #334155"], .dark-mode [style*="color:#334155"],
  .dark-mode [style*="color: #475569"], .dark-mode [style*="color:#475569"] { color: var(--t2) !important; }
  .dark-mode [style*="color: #64748B"], .dark-mode [style*="color:#64748B"],
  .dark-mode [style*="color: #94A3B8"], .dark-mode [style*="color:#94A3B8"] { color: var(--t3) !important; }

  /* ══ STATUS BADGES ══ */
  .dark-mode [style*="background: #ECFDF5"], .dark-mode [style*="background:#ECFDF5"] {
    background: rgba(52,211,153,0.12) !important; color: var(--green) !important; border: 1px solid rgba(52,211,153,0.25) !important;
  }
  .dark-mode [style*="background: #FEF3C7"], .dark-mode [style*="background:#FEF3C7"],
  .dark-mode [style*="background: #FFFBEB"], .dark-mode [style*="background:#FFFBEB"] {
    background: rgba(251,191,36,0.12) !important; color: var(--amber) !important; border: 1px solid rgba(251,191,36,0.25) !important;
  }
  .dark-mode [style*="background: #EFF6FF"], .dark-mode [style*="background:#EFF6FF"],
  .dark-mode [style*="background: #DBEAFE"], .dark-mode [style*="background:#DBEAFE"] {
    background: rgba(96,165,250,0.1) !important; color: var(--blue) !important; border: 1px solid rgba(96,165,250,0.22) !important;
  }
  .dark-mode [style*="background: #FEF2F2"], .dark-mode [style*="background:#FEF2F2"],
  .dark-mode [style*="background: #FFF1F2"], .dark-mode [style*="background:#FFF1F2"] {
    background: rgba(251,113,133,0.1) !important; color: var(--rose) !important; border: 1px solid rgba(251,113,133,0.22) !important;
  }
  .dark-mode [style*="background: #F5F3FF"], .dark-mode [style*="background:#F5F3FF"],
  .dark-mode [style*="background: #EDE9FE"], .dark-mode [style*="background:#EDE9FE"] {
    background: rgba(167,139,250,0.1) !important; color: var(--violet) !important; border: 1px solid rgba(167,139,250,0.22) !important;
  }
  .dark-mode [style*="background: #ECFEFF"], .dark-mode [style*="background:#ECFEFF"] {
    background: rgba(45,212,191,0.1) !important; color: var(--teal) !important; border: 1px solid rgba(45,212,191,0.22) !important;
  }

  /* ══ BUTTONS ══ */
  .dark-mode button { transition: all 0.15s var(--ease) !important; font-family: 'Inter', sans-serif !important; }
  .dark-mode button:active { transform: scale(0.95) !important; }
  .dark-mode button:not([disabled]):hover { box-shadow: 0 0 16px rgba(56,189,248,0.14), 0 2px 8px rgba(0,0,0,0.25) !important; }
  .dark-mode button[style*="background: linear-gradient"]:hover, .dark-mode button[style*="background:linear-gradient"]:hover {
    filter: brightness(1.12) saturate(1.1) !important;
    box-shadow: 0 0 28px rgba(14,165,233,0.4) !important;
  }

  /* ══ MODAL ══ */
  .dark-mode .blitz-modal-content {
    background: rgba(18,24,41,0.97) !important;
    backdrop-filter: blur(28px) saturate(200%) !important;
    -webkit-backdrop-filter: blur(28px) saturate(200%) !important;
    border: 1px solid var(--rim-bright) !important;
    box-shadow: var(--shadow-xl), var(--glow-cyan) !important;
    animation: fadeUp 0.3s var(--ease) !important;
    border-radius: var(--r-xl) !important;
  }

  /* ══ NAV DROPDOWN ══ */
  .dark-mode .nav-dropdown-panel {
    background: rgba(18,24,41,0.98) !important;
    border: 1px solid var(--rim-bright) !important;
    box-shadow: var(--shadow-xl), 0 0 60px rgba(0,0,0,0.6) !important;
    backdrop-filter: blur(24px) !important;
    border-radius: var(--r-md) !important;
  }
  .dark-mode .nav-dropdown-panel button:hover { background: rgba(56,189,248,0.07) !important; color: var(--cyan) !important; }

  /* ══ MOBILE NAV ══ */
  .dark-mode [style*="position: fixed"][style*="background: #FFFFFF"] {
    background: rgba(9,13,26,0.99) !important;
    border-color: var(--rim-bright) !important;
    backdrop-filter: blur(24px) !important;
  }

  /* ══ ACCENT COLOURS ══ */
  .dark-mode [style*="color: #10B981"], .dark-mode [style*="color:#10B981"] { color: var(--green) !important; }
  .dark-mode [style*="color: #059669"], .dark-mode [style*="color:#059669"] { color: var(--teal) !important; }
  .dark-mode [style*="color: #EF4444"], .dark-mode [style*="color:#EF4444"] { color: var(--rose) !important; }
  .dark-mode [style*="color: #F59E0B"], .dark-mode [style*="color:#F59E0B"] { color: var(--amber) !important; }
  .dark-mode [style*="color: #0EA5E9"], .dark-mode [style*="color:#0EA5E9"] { color: var(--cyan) !important; }
  .dark-mode [style*="color: #6366F1"], .dark-mode [style*="color:#6366F1"] { color: var(--indigo) !important; }
  .dark-mode [style*="color: #8B5CF6"], .dark-mode [style*="color:#8B5CF6"] { color: var(--violet) !important; }
  .dark-mode [style*="color: #EC4899"], .dark-mode [style*="color:#EC4899"] { color: var(--pink) !important; }
  .dark-mode [style*="color: #F97316"], .dark-mode [style*="color:#F97316"] { color: var(--orange) !important; }
  .dark-mode [style*="color: #14B8A6"], .dark-mode [style*="color:#14B8A6"] { color: var(--teal) !important; }
  .dark-mode [style*="color: #A3E635"], .dark-mode [style*="color:#A3E635"] { color: var(--lime) !important; }
  .dark-mode [style*="color: #38BDF8"], .dark-mode [style*="color:#38BDF8"] { color: var(--cyan) !important; }

  /* ══ TABLE CONTAINER ══ */
  .dark-mode .blitz-table-responsive {
    border: 1px solid var(--rim) !important;
    border-radius: var(--r-lg) !important;
    overflow: hidden !important;
    box-shadow: var(--shadow-md) !important;
  }

  /* ══ SUMMARY GRID CARDS ══ */
  .dark-mode .blitz-summary > div {
    background: var(--grad-card) !important;
    border: 1px solid var(--rim) !important;
    border-radius: var(--r-lg) !important;
    box-shadow: var(--shadow-md) !important;
    transition: all 0.25s var(--ease) !important;
    position: relative;
    overflow: hidden;
  }
  .dark-mode .blitz-summary > div:hover {
    border-color: var(--rim-bright) !important;
    box-shadow: var(--glow-cyan), var(--shadow-lg) !important;
    transform: translateY(-2px) !important;
  }

  /* ══ RECHARTS ══ */
  .dark-mode [class*="recharts"] text { fill: var(--t3) !important; font-size: 11px !important; }
  .dark-mode [class*="recharts-cartesian-grid"] line { stroke: rgba(99,179,237,0.08) !important; }
  .dark-mode [class*="recharts-tooltip-wrapper"] > div {
    background: var(--elevated) !important;
    border: 1px solid var(--rim-bright) !important;
    border-radius: var(--r-md) !important;
    box-shadow: var(--shadow-lg) !important;
    color: var(--t1) !important;
  }

  /* ══ PROGRESS BARS ══ */
  .dark-mode [style*="background: #E2E8F0"][style*="borderRadius"][style*="height"] { background: var(--elevated) !important; }

  /* ══ GRADIENT CARDS (overview) ══ */
  .dark-mode [style*="background: linear-gradient"][style*="borderRadius"] { border: 1px solid rgba(255,255,255,0.06) !important; }

  /* ══ LOGIN SCREEN ══ */
  .dark-mode [style*="minHeight"][style*="100vh"] {
    background: radial-gradient(ellipse at 20% 50%, rgba(56,189,248,0.06) 0%, transparent 50%),
                radial-gradient(ellipse at 80% 20%, rgba(167,139,250,0.05) 0%, transparent 50%),
                radial-gradient(ellipse at 60% 80%, rgba(251,191,36,0.04) 0%, transparent 50%),
                var(--void) !important;
  }

  /* ══ MONOSPACE (wallet addresses etc) ══ */
  .dark-mode code, .dark-mode [style*="JetBrains Mono"] { color: var(--cyan) !important; }

  /* ══ HR ══ */
  .dark-mode hr { border-color: var(--rim) !important; }
`;
